// Industry-standard debug levels
export enum LogLevel {
  OFF = 0,      // No logging
  ERROR = 1,    // Only errors
  WARN = 2,     // Errors and warnings
  INFO = 3,     // Errors, warnings, and info
  DEBUG = 4,    // All logs including debug
  TRACE = 5     // Most verbose - includes websocket traces
}

// Debug categories for selective filtering
export enum LogCategory {
  GENERAL = 'general',
  API = 'api',
  WEBSOCKET = 'websocket',
  SERVER = 'server',
  UI = 'ui'
}

// Parse log level from environment variable
function parseLogLevel(levelStr: string | undefined): LogLevel {
  if (!levelStr) return LogLevel.OFF
  
  const level = levelStr.toLowerCase()
  switch (level) {
    case 'off': return LogLevel.OFF
    case 'error': return LogLevel.ERROR  
    case 'warn': case 'warning': return LogLevel.WARN
    case 'info': return LogLevel.INFO
    case 'debug': return LogLevel.DEBUG
    case 'trace': case 'verbose': return LogLevel.TRACE
    case 'true': return LogLevel.DEBUG // backward compatibility
    case 'false': return LogLevel.OFF  // backward compatibility
    default: 
      // Try to parse as number
      const num = parseInt(level, 10)
      if (!isNaN(num) && num >= 0 && num <= 5) return num
      return LogLevel.OFF
  }
}

// Parse excluded categories from environment variable
function parseExcludedCategories(categoriesStr: string | undefined): Set<LogCategory> {
  if (!categoriesStr) return new Set()
  
  const categories = categoriesStr.toLowerCase().split(',').map(c => c.trim())
  const excluded = new Set<LogCategory>()
  
  categories.forEach(cat => {
    switch (cat) {
      case 'websocket': case 'ws': excluded.add(LogCategory.WEBSOCKET); break
      case 'api': excluded.add(LogCategory.API); break
      case 'server': excluded.add(LogCategory.SERVER); break
      case 'ui': excluded.add(LogCategory.UI); break
      case 'general': excluded.add(LogCategory.GENERAL); break
    }
  })
  
  return excluded
}

// Get configuration from environment
// Priority: runtime config > build-time env vars
const RUNTIME_LOG_LEVEL = typeof window !== 'undefined' && (window as any).__RUNTIME_CONFIG__?.LOG_LEVEL
const LOG_LEVEL = parseLogLevel(RUNTIME_LOG_LEVEL || (import.meta as any).env?.VITE_LOG_LEVEL || (import.meta as any).env?.VITE_DEBUG)
const EXCLUDED_CATEGORIES = parseExcludedCategories((import.meta as any).env?.VITE_LOG_EXCLUDE)

// Enable development mode logging if in dev and no explicit level set
const EFFECTIVE_LOG_LEVEL = LOG_LEVEL > LogLevel.OFF ? LOG_LEVEL : 
  ((import.meta as any).env?.DEV && LOG_LEVEL === LogLevel.OFF ? LogLevel.INFO : LOG_LEVEL)

/**
 * Leveled debug logging utility with category filtering
 * 
 * Log Levels (industry standard):
 * - OFF (0): No logging  
 * - ERROR (1): Only errors
 * - WARN (2): Errors and warnings
 * - INFO (3): Errors, warnings, and info 
 * - DEBUG (4): All logs including debug
 * - TRACE (5): Most verbose - includes websocket traces
 * 
 * Categories:
 * - general: General application logs
 * - api: API client logs  
 * - websocket: WebSocket connection logs
 * - server: Server utility logs
 * - ui: UI component logs
 */
class Logger {
  private shouldLog(level: LogLevel, category: LogCategory): boolean {
    return EFFECTIVE_LOG_LEVEL >= level && !EXCLUDED_CATEGORIES.has(category)
  }

  private formatMessage(category: LogCategory, ...args: any[]): any[] {
    return [`[${category.toUpperCase()}]`, ...args]
  }

  error(category: LogCategory, ...args: any[]) {
    if (this.shouldLog(LogLevel.ERROR, category)) {
      console.error(...this.formatMessage(category, ...args))
    }
  }

  warn(category: LogCategory, ...args: any[]) {
    if (this.shouldLog(LogLevel.WARN, category)) {
      console.warn(...this.formatMessage(category, ...args))
    }
  }

  info(category: LogCategory, ...args: any[]) {
    if (this.shouldLog(LogLevel.INFO, category)) {
      console.info(...this.formatMessage(category, ...args))
    }
  }

  debug(category: LogCategory, ...args: any[]) {
    if (this.shouldLog(LogLevel.DEBUG, category)) {
      console.log(...this.formatMessage(category, ...args))
    }
  }

  trace(category: LogCategory, ...args: any[]) {
    if (this.shouldLog(LogLevel.TRACE, category)) {
      console.log(...this.formatMessage(category, ...args))
    }
  }

  // Legacy methods for backward compatibility (use general category)
  log(...args: any[]) {
    this.debug(LogCategory.GENERAL, ...args)
  }
}

export const logger = new Logger()

// Convenience functions for specific categories
export const apiLogger = {
  error: (...args: any[]) => logger.error(LogCategory.API, ...args),
  warn: (...args: any[]) => logger.warn(LogCategory.API, ...args),
  info: (...args: any[]) => logger.info(LogCategory.API, ...args),
  debug: (...args: any[]) => logger.debug(LogCategory.API, ...args),
  trace: (...args: any[]) => logger.trace(LogCategory.API, ...args),
}

export const wsLogger = {
  error: (...args: any[]) => logger.error(LogCategory.WEBSOCKET, ...args),
  warn: (...args: any[]) => logger.warn(LogCategory.WEBSOCKET, ...args),
  info: (...args: any[]) => logger.info(LogCategory.WEBSOCKET, ...args),
  debug: (...args: any[]) => logger.debug(LogCategory.WEBSOCKET, ...args),
  trace: (...args: any[]) => logger.trace(LogCategory.WEBSOCKET, ...args),
}

export const serverLogger = {
  error: (...args: any[]) => logger.error(LogCategory.SERVER, ...args),
  warn: (...args: any[]) => logger.warn(LogCategory.SERVER, ...args),
  info: (...args: any[]) => logger.info(LogCategory.SERVER, ...args),
  debug: (...args: any[]) => logger.debug(LogCategory.SERVER, ...args),
  trace: (...args: any[]) => logger.trace(LogCategory.SERVER, ...args),
  /** Check before constructing expensive debug arguments */
  get isDebugEnabled() { return EFFECTIVE_LOG_LEVEL >= LogLevel.DEBUG && !EXCLUDED_CATEGORIES.has(LogCategory.SERVER) },
}

export const uiLogger = {
  error: (...args: any[]) => logger.error(LogCategory.UI, ...args),
  warn: (...args: any[]) => logger.warn(LogCategory.UI, ...args),
  info: (...args: any[]) => logger.info(LogCategory.UI, ...args),
  debug: (...args: any[]) => logger.debug(LogCategory.UI, ...args),
  trace: (...args: any[]) => logger.trace(LogCategory.UI, ...args),
}

// Default export for backward compatibility (general category)
const debug = {
  log: (...args: any[]) => logger.debug(LogCategory.GENERAL, ...args),
  warn: (...args: any[]) => logger.warn(LogCategory.GENERAL, ...args),
  error: (...args: any[]) => logger.error(LogCategory.GENERAL, ...args),
  info: (...args: any[]) => logger.info(LogCategory.GENERAL, ...args),
}

export default debug
